import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { profileApi, type ProfileUser } from '@/services/api';
import { FadeIn } from '@/components/base/Animations';
import MySupplyTab from './components/MySupplyTab';
import MyDemandTab from './components/MyDemandTab';
import AccountSettings from './components/AccountSettings';
import MessageCenter from './components/MessageCenter';
import Favorites from './components/Favorites';
import ContactRecords from './components/ContactRecords';

type TabKey = 'listings' | 'contacts' | 'favorites' | 'messages' | 'settings';

export default function ProfilePage() {
  const [user, setUser] = useState<ProfileUser | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('listings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    profileApi
      .get()
      .then((data) => {
        setUser(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, []);

  const tabs: { key: TabKey; label: string; icon: string; badge?: number }[] = [
    { key: 'listings', label: '我的挂牌', icon: 'ri-file-list-3-line' },
    { key: 'contacts', label: '联系记录', icon: 'ri-contacts-line' },
    { key: 'favorites', label: '收藏夹', icon: 'ri-bookmark-line' },
    { key: 'messages', label: '消息中心', icon: 'ri-mail-line', badge: 2 },
    { key: 'settings', label: '账号设置', icon: 'ri-settings-3-line' },
  ];

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background-50">
        <div className="w-12 h-12 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 text-sm mt-4">{error || '请先登录'}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="mt-4 px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
          >
            前往登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        {/* User header banner */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-4-line text-2xl text-primary-600"></i>
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  {user.companyName || user.contactName}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {user.region && (
                    <span className="flex items-center gap-1 text-foreground-500 text-sm">
                      <i className="ri-map-pin-line text-xs"></i>
                      {user.region}
                    </span>
                  )}
                  {user.region && <span className="text-foreground-300 text-xs">|</span>}
                  <span className="text-foreground-500 text-sm">{user.contactName}</span>
                  <span className="text-foreground-300 text-xs">|</span>
                  <span className="text-foreground-500 text-sm">{user.phone}</span>
                </div>
              </div>
              {/* Account type badge */}
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  {user.accountType === 'company' ? '企业账户' : '个人账户'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content area */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Sidebar tabs - horizontal scroll on mobile, vertical on desktop */}
            <div className="lg:w-56 flex-shrink-0">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0 lg:sticky lg:top-24">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.key
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${tab.icon} text-base`}></i>
                    <span>{tab.label}</span>
                    {tab.badge && tab.badge > 0 && (
                      <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <FadeIn key={activeTab} direction="right">
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8 min-h-[500px]">
                {activeTab === 'listings' && (
                  <div className="space-y-8">
                    <MySupplyTab role="supplier" />
                    <MyDemandTab role="buyer" />
                  </div>
                )}
                {activeTab === 'contacts' && <ContactRecords />}
                {activeTab === 'favorites' && <Favorites />}
                {activeTab === 'messages' && <MessageCenter />}
                {activeTab === 'settings' && <AccountSettings />}
              </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}